import { useState, useEffect, useCallback, useRef, type Dispatch, type SetStateAction } from "react";
import {
  getClientNotes,
  createClientNote,
  deleteClientNote,
} from "@services/requests/housien-services/ClientInsightsService";
import type { ClientNote } from "@services/requests/housien-services/ClientInsightsService";
import { extractApiErrorMessage } from "@/shared/utils/api-error-message";

interface UseClientNotesParams {
  agencyId: string;
  clientId: string;
}

/** Outcome of a note mutation, carrying the API message when it failed. */
export interface NoteMutationResult {
  ok: boolean;
  error?: string;
}

interface UseClientNotesResult {
  notes: ClientNote[];
  isLoading: boolean;
  isError: boolean;
  addNote: (content: string) => Promise<NoteMutationResult>;
  deleteNote: (noteId: string) => Promise<boolean>;
  refetch: () => void;
}

/** Result of a note-create attempt: the note, or the reason it failed. */
type CreateNoteOutcome = { note: ClientNote; error?: undefined } | { note: null; error: unknown };

/**
 * Attempts to create a client note, capturing the failure instead of
 * discarding it so callers can report it to the user.
 *
 * @param agencyId - The agency identifier.
 * @param clientId - The client identifier.
 * @param content - The note content.
 * @returns The created note, or the error that prevented it.
 */
async function tryCreateNote(agencyId: string, clientId: string, content: string): Promise<CreateNoteOutcome> {
  try {
    return { note: await createClientNote(agencyId, clientId, content) };
  } catch (error) {
    return { note: null, error };
  }
}

/**
 * Attempts to delete a client note, swallowing failures.
 *
 * @param agencyId - The agency identifier.
 * @param clientId - The client identifier.
 * @param noteId - The note identifier to delete.
 * @returns True when the note was deleted, false on error.
 */
async function tryDeleteNote(agencyId: string, clientId: string, noteId: string): Promise<boolean> {
  try {
    await deleteClientNote(agencyId, clientId, noteId);
    return true;
  } catch {
    return false;
  }
}

/**
 * Whether both identifiers are present so a notes fetch can run.
 *
 * @param agencyId - The agency identifier.
 * @param clientId - The client identifier.
 * @returns True when both identifiers are non-empty.
 */
function canFetch(agencyId: string, clientId: string): boolean {
  return Boolean(agencyId) && Boolean(clientId);
}

/**
 * Coerces raw fetch data into a note array.
 *
 * @param data - The raw response value.
 * @returns The data as a note array, or an empty array.
 */
function normalizeNotes(data: unknown): ClientNote[] {
  return Array.isArray(data) ? data : [];
}

/**
 * Provides memoized add and delete note mutations with optimistic local
 * state updates.
 *
 * @param agencyId - The agency identifier.
 * @param clientId - The client identifier.
 * @param setNotes - State setter for the note list.
 * @returns The `addNote` and `deleteNote` mutation callbacks.
 */
function useNoteMutations(agencyId: string, clientId: string, setNotes: Dispatch<SetStateAction<ClientNote[]>>) {
  const addNote = useCallback(
    async (content: string): Promise<NoteMutationResult> => {
      const trimmed = content.trim();
      if (!trimmed) return { ok: false };
      const { note, error } = await tryCreateNote(agencyId, clientId, trimmed);
      if (!note) return { ok: false, error: extractApiErrorMessage(error, '') };
      setNotes((prev) => [note, ...prev]);
      return { ok: true };
    },
    [agencyId, clientId, setNotes],
  );

  const deleteNote = useCallback(
    async (noteId: string) => {
      const ok = await tryDeleteNote(agencyId, clientId, noteId);
      if (ok) setNotes((prev) => prev.filter((n) => n.note_id !== noteId));
      return ok;
    },
    [agencyId, clientId, setNotes],
  );

  return { addNote, deleteNote };
}

interface NotesFetchArgs {
  agencyId: string;
  clientId: string;
  cancelRef: { current: { cancel: () => void } | null };
  setNotes: Dispatch<SetStateAction<ClientNote[]>>;
  setIsLoading: (v: boolean) => void;
  setIsError: (v: boolean) => void;
}

/**
 * Loads notes for the client, cancelling any in-flight request first.
 *
 * @param args - Fetch arguments including identifiers, cancel ref, and setters.
 * @returns A promise that resolves when the fetch settles.
 */
async function runNotesFetch(args: NotesFetchArgs): Promise<void> {
  if (!canFetch(args.agencyId, args.clientId)) return;
  args.cancelRef.current?.cancel();
  args.setIsLoading(true);
  args.setIsError(false);
  const req = getClientNotes(args.agencyId, args.clientId);
  args.cancelRef.current = req;
  try {
    args.setNotes(normalizeNotes(await req));
  } catch {
    args.setIsError(true);
    args.setNotes([]);
  } finally {
    args.setIsLoading(false);
  }
}

/**
 * Loads and mutates structured notes for a client Overview.
 *
 * @param params - Hook parameters.
 * @param params.agencyId - The agency identifier.
 * @param params.clientId - The client identifier.
 * @returns Notes, loading/error flags, and add/delete/refetch actions.
 */
export function useClientNotes({
  agencyId,
  clientId,
}: UseClientNotesParams): UseClientNotesResult {
  const shouldFetch = canFetch(agencyId, clientId);
  const [notes, setNotes] = useState<ClientNote[]>([]);
  const [isLoading, setIsLoading] = useState(shouldFetch);
  const [isError, setIsError] = useState(false);
  const cancelRef = useRef<{ cancel: () => void } | null>(null);

  const fetchNotes = useCallback(
    () => runNotesFetch({ agencyId, clientId, cancelRef, setNotes, setIsLoading, setIsError }),
    [agencyId, clientId],
  );

  useEffect(() => {
    void fetchNotes();
    return () => cancelRef.current?.cancel();
  }, [fetchNotes]);

  const { addNote, deleteNote } = useNoteMutations(agencyId, clientId, setNotes);

  return { notes, isLoading, isError, addNote, deleteNote, refetch: fetchNotes };
}
